import { z } from 'zod';

import {
	createLog,
	INVENTORY_SECTIONS,
	LogSeverity,
	Services,
	type ServicesProvider,
	WebsiteProvider,
} from '@ekhodealer/ekho-common';
import { urlSchema } from '@ekhodealer/ekho-common';
import { Box, Form } from '@ekhodealer/ekho-common/rev';

import type {
	ServicesProviderScrapingConfig} from '@ekhodealer/ekho-common';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useCreateInventoryScraper } from '../../api/configuration/inventory/useCreateInventoryScraper';
import { useServiceProgramInfo } from '../../hooks/useServiceProgramInfo';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): Partial<WebsiteScrapingFormData> {
	const scrapingConfig = servicesProvider?.config?.[Services.INVENTORY]?.scrapingConfig;
	if (!scrapingConfig) {
		return {
			allInventoryUrl: '',
			allInventoryUrlPaginationPattern: '',
		};
	}

	return {
		websiteProvider: scrapingConfig.websiteProvider,
		allInventoryUrl: scrapingConfig.allInventoryUrl ?? '',
		allInventoryUrlPaginationPattern: scrapingConfig.allInventoryUrlPaginationPattern ?? '',
	};
}

/**
 * Helper function to get scraping config from form data
 */
function getConfigFromFormData(data: WebsiteScrapingFormData): ServicesProviderScrapingConfig {
	return {
		enabled: true,
		websiteProvider: data.websiteProvider,
		allInventoryUrl: data.allInventoryUrl.trim(),
		allInventoryUrlPaginationPattern: data.allInventoryUrlPaginationPattern.trim(),
	};
}

const websiteScrapingSchema = z.object({
	websiteProvider: z.nativeEnum(WebsiteProvider, {
		required_error: 'Please select a website provider',
		invalid_type_error: 'Please select a website provider',
	}),
	allInventoryUrl: urlSchema,
	allInventoryUrlPaginationPattern: z.string().default(''),
});

type WebsiteScrapingFormData = z.infer<typeof websiteScrapingSchema>;

/**
 * Component for managing Website Scraping Configuration
 */
export function WebsiteScrapingForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { mutateAsync: createInventoryScraper } = useCreateInventoryScraper();
	const programInfo = useServiceProgramInfo(INVENTORY_SECTIONS.SCRAPING);

	// Early return if subsection is not part of the program or is Ekho-only
	if (!servicesProvider || !programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (data: WebsiteScrapingFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await createInventoryScraper({
				scrapingConfig: getConfigFromFormData(data),
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to create scraping config: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	const providerOptions: { id: WebsiteProvider; label: string }[] = [
		{ id: WebsiteProvider.DEALER_SPIKE, label: 'Dealer Spike' },
		{ id: WebsiteProvider.PIXEL_MOTION, label: 'PixelMotion' },
		{ id: WebsiteProvider.DEALER_ACCELERATE, label: 'Dealer Accelerate' },
		{ id: WebsiteProvider.MOTION, label: 'Motion' },
		{ id: WebsiteProvider.OTHER, label: 'Other' },
	];

	return (
		<Box padding='24' background='primary' rounding='12' elevation='0'>
			<Form
				name='website-scraping-config'
				schema={websiteScrapingSchema}
				defaultValues={getDefaultValues(servicesProvider)}
				onSubmit={handleSubmit}
				title='Add your website'
				titleMaxWidth='100%'
				showSaveAndClear>
				<Form.Field.Dropdown
					schemaField='websiteProvider'
					labelText='Provider'
					descriptionText='Select your website provider'
					required
					options={providerOptions}
					placeholder='Select provider'
					buttonVariant='outline'
				/>
				<Form.Field.Text
					schemaField='allInventoryUrl'
					labelText='URL'
					descriptionText='This should be your inventory listing page with no filters applied'
					required
					size='large'
					placeholder='https://'
				/>
				<Box padding='20' background='secondary'>
					<Form.Field.Text
						schemaField='allInventoryUrlPaginationPattern'
						labelText='Pagination Pattern'
						descriptionText='URL pattern for pagination (e.g., https://example.com/inventory/page/{page})'
						size='large'
						isReadOnly
						placeholder='https://example.com/inventory/page/{page}'
					/>
				</Box>
			</Form>
		</Box>
	);
}
